import React, { useState, useRef, useCallback, useEffect } from 'react';
import CableCard from '../components/CableCard';
import './CablesDesign.css';

const AGAM_PRODUCTS = [
  'Agam various products',
  'Agam Autopilot',
  'Agam MegH7',
  'Agam RTK-GNSS Base',
  'Agam RTK-F9P L1/L2',
  'Agam RTK-F9P L1/L5',
  'Agam Solo Flight Controller',
  'Agam FloRange',
  'Agam CAN-GNSS-M9N',
];

const STORAGE_KEY = 'agam_cables_design_v1';

const makeCard = (id) => ({
  id,
  fromConnector: '',
  toConnector: '',
  numCables: 1,
  wireColors: [],
  wirePinFrom: [],
  wirePinTo: [],
  cableLength: '',
  noteText: '',
});

/* Load saved state from localStorage */
function loadSaved() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

const CablesDesign = () => {
  const saved = loadSaved();

  const [selectedProduct, setSelectedProduct] = useState(saved?.selectedProduct || '');
  const [cableCards,      setCableCards]      = useState(saved?.cableCards      || []);
  const [productError,    setProductError]    = useState(false);
  const canvasRef = useRef(null);

  /* Persist to localStorage whenever data changes */
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ selectedProduct, cableCards }));
    } catch { /* quota exceeded – ignore */ }
  }, [selectedProduct, cableCards]);

  /* Derived count */
  const numTypes = cableCards.length;

  /* ── Add N cable type cards without wiping existing ones ── */
  const addCards = useCallback((count) => {
    setCableCards((prev) => {
      const nextId = prev.length === 0 ? 0 : Math.max(...prev.map(c => c.id)) + 1;
      const added = Array.from({ length: count }, (_, i) => makeCard(nextId + i));
      return [...prev, ...added];
    });
  }, []);

  /* ── Handle the numeric-input field (set exact count, preserve existing data) ── */
  const handleSetCount = useCallback((val) => {
    const parsed = parseInt(val);
    if (isNaN(parsed) || parsed < 1 || parsed > 50) return;
    setCableCards((prev) => {
      if (parsed > prev.length) {
        const nextId = prev.length === 0 ? 0 : Math.max(...prev.map(c => c.id)) + 1;
        const added = Array.from({ length: parsed - prev.length }, (_, i) => makeCard(nextId + i));
        return [...prev, ...added];
      }
      // shrink — keep existing data, just drop from the end
      return prev.slice(0, parsed);
    });
  }, []);

  const updateCard = useCallback((id, data) => {
    setCableCards((prev) => prev.map((c) => (c.id === id ? { ...c, ...data } : c)));
  }, []);

  /* ── Clear all ── */
  const handleClearAll = () => {
    if (window.confirm('Clear all cable cards? This cannot be undone.')) {
      setCableCards([]);
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  /* ── PDF ── */
  const handleDownloadPDF = () => {
    if (!selectedProduct) {
      setProductError(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setProductError(false);

    const heading = document.querySelector('.canvas-heading');
    const meta    = document.querySelector('.canvas-meta');
    const targets = document.querySelectorAll('.pdf-target');

    let cardsHTML = '';
    targets.forEach((el, i) => {
      cardsHTML += `<div class="print-card">
        ${el.innerHTML}
      </div>`;
    });

    const totalCables = cableCards.reduce((sum, c) => sum + (parseInt(c.numCables) || 1), 0);
    const typesCount  = cableCards.length;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>Cables for ${selectedProduct}</title>
  <link href="https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&family=Rajdhani:wght@300;400;600&family=Share+Tech+Mono&display=swap" rel="stylesheet">
  <style>
    *{margin:0;padding:0;box-sizing:border-box;}
    body{background:#f2f5f9;color:#1a2a3a;font-family:'Rajdhani',sans-serif;padding:36px;}
    @media print{body{padding:2px;background:#fff;}}
    .canvas-heading{text-align:center;margin-bottom:2px;padding:4px 0 2px;}
    .canvas-heading-line{height:1px;background:#b0c4d8;margin:4px 0;}
    .canvas-title{font-family:'Orbitron',sans-serif;font-size:16px;color:#1a2a3a;font-weight:800;letter-spacing:.08em;}
    .canvas-product-name{color:#0055aa;}
    .canvas-meta{display:flex;justify-content:space-between;align-items:center;font-family:'Share Tech Mono',monospace;font-size:10px;color:#6680a0;margin-bottom:10px;padding:0 4px;}
    .canvas-meta-summary{font-family:'Rajdhani',sans-serif;font-size:10px;font-weight:700;color:#1a3a6a;background:#e8f0ff;border:1px solid #99b0dd;border-radius:6px;padding:5px 14px;letter-spacing:.03em;}
    .canvas-meta-summary strong{color:#002288;}
    .print-card{border:1px solid #aebdcc;border-radius:8px;padding:10px 36px;margin-bottom:8px;background:#fff;page-break-inside:avoid;box-shadow:2px 4px 8px rgba(0,0,0,0.25);}
    .print-card-index{font-family:'Orbitron',sans-serif;font-size:8px;color:#0055aa;letter-spacing:.15em;margin-bottom:10px;font-weight:700;}
    .card-right-header{display:flex;align-items:center;justify-content:space-between;margin-bottom:4px;padding-bottom:4px;border-bottom:1px solid #dde6f0;}
    .card-right-title{font-family:'Share Tech Mono',monospace;font-size:10px;color:#6680a0;letter-spacing:.12em;text-transform:uppercase;}
    .card-right-meta{font-family:'Share Tech Mono',monospace;font-size:10px;color:#0055aa;background:#e6f0ff;border:1px solid #99aacc;border-radius:4px;padding:2px 8px;}
    .cable-diagram-wrapper{display:flex;flex-direction:column;gap:10px;}
    .cable-diagram-svg{width:100%;height:auto;}
    .diagram-info-bar{display:flex;gap:10px;margin-bottom:6px;}
    .info-badge{display:inline-flex;align-items:center;gap:5px;padding:5px 14px;border-radius:5px;font-family:'Share Tech Mono',monospace;font-size:11px;font-weight:600;}
    .length-badge{background:#e6f0ff;border:1.5px solid #3377cc;color:#1a3388;}
    .length-badge strong{color:#001177;font-size:13px;}
    .qty-badge{background:#fff0e6;border:1.5px solid #cc6600;color:#663300;}
    .qty-badge strong{color:#441100;font-size:13px;}
    .wire-legend{display:flex;flex-wrap:wrap;gap:8px 14px;padding-top:10px;border-top:1px solid #dde;}
    .legend-item{display:flex;align-items:center;gap:7px;}
    .legend-swatch{width:30px;height:9px;border-radius:4px;display:inline-block;}
    .legend-text{display:flex;flex-direction:column;gap:2px;}
    .legend-pin{font-family:'Share Tech Mono',monospace;font-size:9px;color:#445566;line-height:1;}
    .legend-color-name{font-family:'Share Tech Mono',monospace;font-size:9px;font-weight:700;line-height:1;}
    .diagram-note{margin-top:10px;padding:9px 13px;background:#fffbe6;border:1px solid #d4aa00;border-left:4px solid #aa7700;border-radius:4px;font-size:12px;line-height:1.5;color:#3a2800;}
    .note-label{font-family:'Share Tech Mono',monospace;font-size:10px;font-weight:800;color:#7a5200;letter-spacing:.1em;margin-right:5px;}
    .diagram-placeholder{display:flex;align-items:center;justify-content:center;padding:20px;color:#8899aa;font-family:'Share Tech Mono',monospace;font-size:11px;}
  </style>
</head>
<body>
  ${heading ? heading.outerHTML : ''}
  <div class="canvas-meta">
    <div class="canvas-meta-summary">
      <strong>${typesCount}</strong> different cable type${typesCount !== 1 ? 's' : ''}
      &nbsp;·&nbsp;
      Total of <strong>${totalCables}</strong> cable${totalCables !== 1 ? 's' : ''} in one set
    </div>
    <span class="canvas-date">${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
  </div>
  ${cardsHTML}
</body>
</html>`);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 900);
  };

  /* ── Total cables across all cards ── */
  const totalCables = cableCards.reduce((sum, c) => sum + (parseInt(c.numCables) || 1), 0);

  return (
    <div className="cables-design-page">
      {/* Page Header */}
      <div className="cd-header">
        <div className="page-breadcrumb">
          <span>HOME</span><span className="sep">/</span>
          <span className="current">CABLES DESIGN</span>
        </div>
        <h1 className="cd-title">
          <span className="title-glyph">⟜</span>
          Design Your Cables
        </h1>
        <p className="cd-subtitle">Configure precision cable assemblies for AGAM robotics hardware</p>
      </div>

      {/* Top Form */}
      <div className="cd-form-panel">
        <div className="form-grid">
          {/* Product Select */}
          <div className={`form-field ${productError ? 'field-error' : ''}`}>
            <label className="field-label">
              <span className="label-icon">◈</span>
              AGAM Product <span className="required-star">*</span>
            </label>
            <div className="select-wrapper">
              <select value={selectedProduct}
                onChange={(e) => { setSelectedProduct(e.target.value); setProductError(false); }}
                className="futuristic-select">
                <option value="">— Select Product —</option>
                {AGAM_PRODUCTS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <span className="select-arrow">▾</span>
            </div>
            {productError && <span className="error-msg">⚠ Please select a product before downloading</span>}
          </div>

          {/* Cable type count controls */}
          <div className="form-field">
            <label className="field-label">
              <span className="label-icon">≋</span>
              Number of Different Cable Types
            </label>
            <div className="count-controls">
              <div className="select-wrapper combo-wrapper">
                <input type="number" min="1" max="50"
                  value={numTypes || ''}
                  onChange={(e) => handleSetCount(e.target.value)}
                  placeholder="Type a number"
                  className="futuristic-input" />
                <select value={numTypes || ''}
                  onChange={(e) => handleSetCount(e.target.value)}
                  className="futuristic-select combo-select">
                  <option value="">▾</option>
                  {Array.from({ length: 50 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                  ))}
                </select>
              </div>
              <button className="add-one-btn" onClick={() => addCards(1)}
                disabled={numTypes >= 50}
                title="Add one more cable type">
                + Add 1 more type
              </button>
              {cableCards.length > 0 && (
                <button className="clear-btn" onClick={handleClearAll} title="Clear all cards">
                  ✕ Clear all
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Canvas */}
      {cableCards.length > 0 && (
        <div className="canvas-section">
          {/* Toolbar */}
          <div className="canvas-toolbar">
            <div className="canvas-toolbar-left">
              {selectedProduct
                ? <span className="canvas-info-text">
                    Designing for: <strong>{selectedProduct}</strong>
                    &nbsp;·&nbsp;
                    <strong>{numTypes}</strong> type{numTypes !== 1 ? 's' : ''},&nbsp;
                    <strong>{totalCables}</strong> cable{totalCables !== 1 ? 's' : ''} total
                  </span>
                : <span className="canvas-info-text warn">⚠ Select a product to enable PDF download</span>
              }
            </div>
            <button className="download-btn top-download" onClick={handleDownloadPDF}>
              <span className="dl-icon">⬇</span> Download PDF
            </button>
          </div>

          {/* Canvas area */}
          <div id="cable-canvas-area" className="cable-canvas-area" ref={canvasRef}>
            <div className="canvas-heading">
              <div className="canvas-heading-line" />
              <h2 className="canvas-title">
                Cables for <span className="canvas-product-name">{selectedProduct || '___________'}</span>
              </h2>
              <div className="canvas-heading-line" />
            </div>
            <div className="canvas-meta">
              <span>
                <strong>{numTypes}</strong> different cable type{numTypes !== 1 ? 's' : ''}
                &nbsp;·&nbsp; Total of <strong>{totalCables}</strong> cable{totalCables !== 1 ? 's' : ''} in one set
              </span>
              <span className="canvas-date">
                {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
              </span>
            </div>

            <div className="cable-cards-list">
              {cableCards.map((card, idx) => (
                <CableCard key={card.id} index={idx} card={card} onUpdate={updateCard} />
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="canvas-footer">
            <button className="add-one-btn footer-add" onClick={() => addCards(1)}>
              + Add 1 more cable type
            </button>
            <button className="download-btn bottom-download" onClick={handleDownloadPDF}>
              <span className="dl-icon">⬇</span> Download as PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CablesDesign;
